import React, { useState, useEffect } from 'react';
import { FaEdit } from 'react-icons/fa';
import ApiServices from '../services/ApiServices';
import { ShowSuccessToast, ShowDangerToast } from '../services/toast';

const AdminProfile = () => {
  const [profile, setProfile] = useState({
    username: '',
    email: '',
    profilePicture: '',
  });
  const [credentials, setCredentials] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [profilePic, setProfilePic] = useState(null);
  const [totalStores, setTotalStores] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const userResponse = await ApiServices.getData('/users/me');
        setProfile({
          username: userResponse.data.username,
          email: userResponse.data.email,
          profilePicture:
            userResponse.data.profilePicture ||
            'https://images.pexels.com/photos/771742/pexels-photo-771742.jpeg?auto=compress&cs=tinysrgb&w=600',
        });
        setCredentials({
          username: userResponse.data.username,
          email: userResponse.data.email,
          password: '',
          confirmPassword: '',
        });
        setProfilePic(
          userResponse.data.profilePicture ||
            'https://images.pexels.com/photos/771742/pexels-photo-771742.jpeg?auto=compress&cs=tinysrgb&w=600'
        );

        const storesResponse = await ApiServices.getData('/stores');
        setTotalStores(storesResponse.data.length);
        setLoading(false);
      } catch (error) {
        ShowDangerToast('Failed to load profile or stores');
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleCredentialChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  const handleProfilePicChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePic(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfile({ ...profile, profilePicture: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCredentialSubmit = async (e) => {
    e.preventDefault();
    if (credentials.password !== credentials.confirmPassword) {
      ShowDangerToast('Passwords do not match');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('username', credentials.username);
      formData.append('email', credentials.email);
      if (credentials.password) {
        formData.append('password', credentials.password);
      }
      if (profilePic && typeof profilePic !== 'string') {
        formData.append('profilePicture', profilePic);
      }

      const updatedUser = await ApiServices.postFormData(
        '/users/update-superadmin',
        formData
      );
      setProfile({
        username: updatedUser.data.username,
        email: updatedUser.data.email,
        profilePicture: updatedUser.data.profilePicture,
      });
      setCredentials({
        username: updatedUser.data.username,
        email: updatedUser.data.email,
        password: '',
        confirmPassword: '',
      });
      ShowSuccessToast('Credentials updated successfully');
    } catch (error) {
      ShowDangerToast('Failed to update credentials');
    }
  };

  if (loading) {
    return <div className="text-gray-800">Loading...</div>;
  }

  return (
    <div className="min-h-screen p-6 bg-white w-full h-full">
      <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
        <div className="card p-6 w-full">
          <h3 className="text-lg font-semibold mb-4 text-navy-800">
            Admin Profile
          </h3>
          <div className="flex flex-col items-center">
            <div className="relative w-32 h-32 mb-4">
              <img
                src={profile.profilePicture}
                alt="Profile"
                className="w-full h-full rounded-full object-cover"
              />
              <label
                htmlFor="profile-pic-upload"
                className="absolute bottom-0 right-0 p-2 rounded-full cursor-pointer hover:opacity-80 bg-navy-500 text-white"
              >
                <FaEdit />
              </label>
              <input
                id="profile-pic-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleProfilePicChange}
              />
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-500">Username</p>
              <p className="text-lg font-medium text-gray-900">{profile.username}</p>
              <p className="text-sm mt-2 text-gray-500">Email</p>
              <p className="text-lg font-medium text-gray-900">{profile.email}</p>
            </div>
            <div className="mt-6 w-full">
              <p className="text-sm text-gray-500">Total Stores</p>
              <p className="text-2xl font-bold text-navy-800">{totalStores}</p>
            </div>
          </div>
        </div>

        <div className="card p-6 w-full">
          <h3 className="text-lg font-semibold mb-4 text-navy-800">
            Update Credentials
          </h3>
          <form onSubmit={handleCredentialSubmit} className="space-y-4">
            {[
              { label: 'Username', type: 'text', name: 'username', required: true },
              { label: 'Email', type: 'email', name: 'email', required: true },
              { label: 'New Password', type: 'password', name: 'password' },
              { label: 'Confirm Password', type: 'password', name: 'confirmPassword' },
            ].map((field, index) => (
              <div key={index}>
                <label className="input-label">
                  {field.label}
                </label>
                <input
                  type={field.type}
                  name={field.name}
                  value={credentials[field.name]}
                  onChange={handleCredentialChange}
                  required={field.required}
                  className="input-field"
                />
              </div>
            ))}

            <button
              type="submit"
              className="btn-primary"
            >
              Update Credentials
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminProfile;
